import type { ApiClient } from './client';

export interface LimitedDesignationSupply {
  issued: number;
  remaining: number;
  edition_size: number;
  next_ordinal: number | null;
  edition_complete: boolean;
}

export interface LimitedDesignationDefinition {
  id: number;
  key: string;
  name: string;
  description?: string | null;
  public_blurb?: string | null;
  subject_type: string;
  issuer_label: string;
  edition_size: number;
  status: string;
  supply?: LimitedDesignationSupply;
}

export interface LimitedDesignationIssuance {
  id?: number;
  public_id: string;
  definition_key?: string;
  name: string;
  ordinal: number;
  edition_size: number;
  ordinal_label?: string;
  title?: string;
  description?: string | null;
  public_blurb?: string | null;
  issuer_label: string;
  authority_class?: string;
  issued_at: string;
  status: string;
  subject_type?: string;
  subject_id?: number;
  reason?: string | null;
  public_reason?: string | null;
  vehicle?: {
    id?: number;
    year?: number | null;
    make?: string | null;
    model?: string | null;
    nickname?: string | null;
    username?: string | null;
    public_slug?: string | null;
    hero_image_url?: string | null;
  } | null;
}

export interface LimitedDesignationVehicleSearchResult {
  id: number;
  year?: number | null;
  make?: string | null;
  model?: string | null;
  nickname?: string | null;
  username?: string | null;
  public_slug?: string | null;
  is_public?: boolean;
  owner_name?: string;
  hero_image_url?: string | null;
  public_url?: string | null;
}

export const limitedDesignationMethods = {
  async adminLimitedDesignationOverview(): Promise<{
    flags: Record<string, boolean>;
    definitions: LimitedDesignationDefinition[];
  }> {
    const response = await this.makeRequest<{
      flags: Record<string, boolean>;
      definitions: LimitedDesignationDefinition[];
    }>(this.getAdminEndpoint('/admin/limited_designations.php'));
    if (!response.success) throw new Error(response.message || 'Failed to load designations');
    return response.data || { flags: {}, definitions: [] };
  },

  async adminLimitedDesignationIssuances(definitionId?: number): Promise<{
    definition: LimitedDesignationDefinition | null;
    supply: LimitedDesignationSupply | null;
    issuances: LimitedDesignationIssuance[];
  }> {
    const qs = definitionId ? `?view=issuances&definition_id=${definitionId}` : '?view=issuances';
    const response = await this.makeRequest<{
      definition: LimitedDesignationDefinition | null;
      supply: LimitedDesignationSupply | null;
      issuances: LimitedDesignationIssuance[];
    }>(this.getAdminEndpoint(`/admin/limited_designations.php${qs}`));
    if (!response.success) throw new Error(response.message || 'Failed to load issuances');
    return response.data || { definition: null, supply: null, issuances: [] };
  },

  async adminLimitedDesignationSearchVehicles(
    q: string,
    limit = 20
  ): Promise<LimitedDesignationVehicleSearchResult[]> {
    const response = await this.makeRequest<LimitedDesignationVehicleSearchResult[]>(
      this.getAdminEndpoint(
        `/admin/limited_designations.php?view=search_vehicles&q=${encodeURIComponent(q)}&limit=${limit}`
      )
    );
    if (!response.success) throw new Error(response.message || 'Search failed');
    return response.data || [];
  },

  async adminLimitedDesignationEligibility(
    definitionId: number,
    vehicleId: number
  ): Promise<{
    eligible: boolean;
    reason?: string | null;
    next_ordinal?: number | null;
    supply?: LimitedDesignationSupply;
  }> {
    const response = await this.makeRequest<{
      eligible: boolean;
      reason?: string | null;
      next_ordinal?: number | null;
      supply?: LimitedDesignationSupply;
    }>(
      this.getAdminEndpoint(
        `/admin/limited_designations.php?view=eligibility&definition_id=${definitionId}&vehicle_id=${vehicleId}`
      )
    );
    if (!response.success) throw new Error(response.message || 'Eligibility check failed');
    return response.data || { eligible: false };
  },

  async adminLimitedDesignationSetFlags(flags: Record<string, boolean>): Promise<Record<string, boolean>> {
    const response = await this.makeRequest<Record<string, boolean>>(
      this.getAdminEndpoint('/admin/limited_designations.php'),
      {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ action: 'set_flags', flags }),
      }
    );
    if (!response.success) throw new Error(response.message || 'Failed to update flags');
    return response.data || {};
  },

  async adminLimitedDesignationIssue(payload: {
    definition_id: number;
    vehicle_id: number;
    reason?: string;
    public_reason?: string;
  }): Promise<LimitedDesignationIssuance> {
    const response = await this.makeRequest<LimitedDesignationIssuance>(
      this.getAdminEndpoint('/admin/limited_designations.php'),
      {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ action: 'issue', confirm: 'ISSUE', ...payload }),
      }
    );
    if (!response.success) throw new Error(response.message || 'Issuance failed');
    if (!response.data) throw new Error('Issuance failed');
    return response.data;
  },

  async adminLimitedDesignationRevoke(issuanceId: number, reason: string): Promise<LimitedDesignationIssuance> {
    const response = await this.makeRequest<LimitedDesignationIssuance>(
      this.getAdminEndpoint('/admin/limited_designations.php'),
      {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          action: 'revoke',
          confirm: 'REVOKE',
          issuance_id: issuanceId,
          reason,
        }),
      }
    );
    if (!response.success) throw new Error(response.message || 'Revoke failed');
    if (!response.data) throw new Error('Revoke failed');
    return response.data;
  },

  async adminLimitedDesignationCorrect(payload: {
    issuance_id: number;
    vehicle_id: number;
    reason: string;
  }): Promise<LimitedDesignationIssuance> {
    const response = await this.makeRequest<LimitedDesignationIssuance>(
      this.getAdminEndpoint('/admin/limited_designations.php'),
      {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ action: 'correct', confirm: 'CORRECT', ...payload }),
      }
    );
    if (!response.success) throw new Error(response.message || 'Correction failed');
    if (!response.data) throw new Error('Correction failed');
    return response.data;
  },

  async getVehicleDesignations(vehicleId: number): Promise<{
    designations: LimitedDesignationIssuance[];
    primary: LimitedDesignationIssuance | null;
  }> {
    const response = await this.makeRequest<{
      designations: LimitedDesignationIssuance[];
      primary: LimitedDesignationIssuance | null;
    }>(`/vehicle_designations.php?vehicle_id=${vehicleId}`);
    if (!response.success) throw new Error(response.message || 'Failed to load designations');
    return response.data || { designations: [], primary: null };
  },

  async getFoundingVehiclesCollection(): Promise<{
    definition: {
      key: string;
      name: string;
      public_blurb?: string | null;
      edition_size: number;
      issuer_label: string;
    } | null;
    supply: { issued: number; edition_size: number } | null;
    issuances: LimitedDesignationIssuance[];
  }> {
    const response = await this.makeRequest<{
      definition: {
        key: string;
        name: string;
        public_blurb?: string | null;
        edition_size: number;
        issuer_label: string;
      } | null;
      supply: { issued: number; edition_size: number } | null;
      issuances: LimitedDesignationIssuance[];
    }>('/limited_designation_public.php?key=founding_vehicle');
    if (!response.success) throw new Error(response.message || 'Failed to load Founding Vehicles');
    return response.data || { definition: null, supply: null, issuances: [] };
  },
} satisfies ThisType<ApiClient>;

export type LimitedDesignationMethods = typeof limitedDesignationMethods;
